import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../context/LangContext";
import Layout from "../components/Layout";
import {
  MessageSquare, Globe, CheckCircle2, Bot,
  Clock, ArrowRight, Users, Wifi, Monitor, Smartphone, Zap, ExternalLink
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_LABELS = {
  active: "Aktif",
  waiting_agent: "Beklemede",
  open: "Açık",
  closed: "Kapalı",
  resolved: "Çözüldü",
  pending: "Beklemede",
};

function StatCard({ icon: Icon, label, value, color, trend }) {
  return (
    <div className="bg-white rounded-xl border border-slate-100 p-6 card-hover animate-fade-in-up" data-testid="stat-card">
      <div className="flex items-start justify-between mb-4">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${color}`}>
          <Icon size={20} />
        </div>
        {trend !== undefined && (
          <span className="text-xs text-emerald-600 bg-emerald-50 px-2 py-1 rounded-full font-medium">
            +{trend}%
          </span>
        )}
      </div>
      <div className="text-2xl font-heading font-bold text-slate-900 mb-1">{value}</div>
      <div className="text-sm text-slate-500">{label}</div>
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [recentConvs, setRecentConvs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [liveVisitors, setLiveVisitors] = useState({ online: 0, visitors: [] });
  const liveTimerRef = useRef(null);

  const fetchLive = () =>
    axios.get(`${API}/analytics/online`).then(({ data }) => setLiveVisitors(data)).catch(() => {});

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/analytics?period=7d`),
      axios.get(`${API}/conversations?limit=5`)
    ]).then(([analyticsRes, convsRes]) => {
      setStats(analyticsRes.data);
      setRecentConvs(convsRes.data);
    }).catch(console.error).finally(() => setLoading(false));

    fetchLive();
    liveTimerRef.current = setInterval(fetchLive, 30000);
    return () => clearInterval(liveTimerRef.current);
  }, []);

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-full">
          <div className="text-slate-400 animate-pulse">{t("common.loading")}</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-4 md:p-8 max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8 animate-fade-in-up">
          <h1 className="font-heading text-3xl font-bold text-slate-900">
            {t("dashboard.welcome")}, {user?.name?.split(" ")[0]}!
          </h1>
          <p className="text-slate-500 mt-1">Canlı Destek Dashboard — Son 7 gün</p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-8 stagger-children">
          <StatCard
            icon={MessageSquare}
            label={t("dashboard.total_convs")}
            value={stats?.total_conversations || 0}
            color="bg-blue-50 text-blue-600"
          />
          <StatCard
            icon={Clock}
            label={t("dashboard.open_convs")}
            value={stats?.open_conversations || 0}
            color="bg-amber-50 text-amber-600"
          />
          <StatCard
            icon={Bot}
            label={t("dashboard.ai_handled")}
            value={stats?.ai_handled || 0}
            color="bg-emerald-50 text-emerald-600"
          />
          <StatCard
            icon={CheckCircle2}
            label={t("dashboard.resolved")}
            value={stats?.resolved_conversations || 0}
            color="bg-blue-50 text-blue-600"
          />
        </div>

        {/* Canlı Ziyaretçiler */}
        <div className="bg-white rounded-xl border border-slate-100 p-6 mb-6 animate-fade-in-up" data-testid="live-visitors-section">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2.5">
              <span className="relative flex h-2.5 w-2.5">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500"></span>
              </span>
              <h2 className="font-semibold text-slate-900 text-sm">Canlı Ziyaretçiler</h2>
              <span className="text-xs font-bold bg-emerald-50 text-emerald-700 px-2 py-0.5 rounded-full">
                {liveVisitors.online}
              </span>
            </div>
            <button
              onClick={() => navigate("/analytics")}
              className="text-blue-600 text-xs font-medium hover:text-blue-700 flex items-center gap-1"
            >
              Tümü <ArrowRight size={12} />
            </button>
          </div>

          {liveVisitors.visitors.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-6 text-slate-300">
              <Wifi size={28} className="mb-2" />
              <p className="text-xs text-slate-400">Şu an aktif ziyaretçi yok</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-left text-slate-400 border-b border-slate-50">
                    <th className="pb-2 font-medium">Konum</th>
                    <th className="pb-2 font-medium">Sayfa</th>
                    <th className="pb-2 font-medium">Kaynak</th>
                    <th className="pb-2 font-medium">Cihaz</th>
                    <th className="pb-2 font-medium text-right">Süre</th>
                  </tr>
                </thead>
                <tbody>
                  {liveVisitors.visitors.slice(0, 8).map((v, i) => (
                    <tr key={v.session_id || i} className="border-b border-slate-50 hover:bg-slate-50 transition-colors">
                      <td className="py-2 pr-3">
                        <div className="flex items-center gap-1.5">
                          <span className="text-sm">{v.flag || "🌍"}</span>
                          <span className="text-slate-700 font-medium truncate max-w-[90px]">{v.city !== "—" ? v.city : v.country}</span>
                        </div>
                      </td>
                      <td className="py-2 pr-3 max-w-[180px]">
                        {v.current_page && v.current_page !== "—" ? (
                          <a
                            href={v.current_page}
                            target="_blank"
                            rel="noreferrer"
                            title={v.current_page}
                            className="flex items-center gap-1 text-blue-600 hover:underline text-xs group/pg"
                          >
                            <Globe size={11} className="flex-shrink-0 text-slate-400" />
                            <span className="truncate max-w-[150px]">
                              {v.current_title ||
                                (v.current_page.replace(/^https?:\/\/[^/]+/, "") || "/")}
                            </span>
                            <ExternalLink size={9} className="flex-shrink-0 opacity-0 group-hover/pg:opacity-60 transition-opacity" />
                          </a>
                        ) : (
                          <span className="text-slate-300 text-xs">—</span>
                        )}
                      </td>
                      <td className="py-2 pr-3">
                        <span className="text-slate-500 truncate max-w-[100px] block">
                          {v.referrer === "—" ? "Direkt" : v.referrer.replace(/^https?:\/\/(www\.)?/, "").split("/")[0]}
                        </span>
                      </td>
                      <td className="py-2 pr-3">
                        <div className="flex items-center gap-1 text-slate-400">
                          {v.device === "Mobile" ? <Smartphone size={11} /> : <Monitor size={11} />}
                          <span>{v.browser}</span>
                        </div>
                      </td>
                      <td className="py-2 text-right text-slate-400">
                        {v.minutes_on_site < 1 ? "<1d" : `${v.minutes_on_site}d`}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Recent Conversations */}
          <div className="lg:col-span-2 bg-white rounded-xl border border-slate-100 p-6 animate-fade-in-up">
            <div className="flex items-center justify-between mb-5">
              <h2 className="font-heading font-semibold text-slate-900">{t("dashboard.recent_convs")}</h2>
              <button
                onClick={() => navigate("/inbox")}
                className="text-blue-600 text-sm font-medium hover:text-blue-700 flex items-center gap-1 transition-colors"
                data-testid="view-all-convs"
              >
                {t("dashboard.view_all")} <ArrowRight size={14} />
              </button>
            </div>

            {recentConvs.length === 0 ? (
              <div className="text-center py-12">
                <MessageSquare size={32} className="mx-auto text-slate-300 mb-3" />
                <p className="text-slate-500 text-sm">{t("dashboard.no_convs")}</p>
              </div>
            ) : (
              <div className="space-y-3">
                {recentConvs.map((conv) => (
                  <div
                    key={conv.id}
                    onClick={() => navigate(`/inbox?conv=${conv.id}`)}
                    className="flex items-center gap-3 p-3 rounded-xl hover:bg-slate-50 cursor-pointer transition-colors"
                    data-testid={`conv-row-${conv.id}`}
                  >
                    <div className="w-9 h-9 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center text-white text-sm font-bold flex-shrink-0">
                      {conv.visitor_name?.[0]?.toUpperCase() || "V"}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-slate-800 text-sm truncate">{conv.visitor_name || t("common.visitor")}</span>
                        <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full status-${conv.status}`}>
                          {(conv.status === 'active' || conv.status === 'waiting_agent') && (
                            <span className="w-1.5 h-1.5 rounded-full bg-orange-500 animate-pulse flex-shrink-0" />
                          )}
                          {STATUS_LABELS[conv.status] || conv.status}
                        </span>
                      </div>
                      <p className="text-xs text-slate-500 truncate mt-0.5">
                        {conv.last_message?.content || "..."}
                      </p>
                    </div>
                    <div className="text-xs text-slate-400 flex-shrink-0">
                      {conv.site_name}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Quick Links */}
          <div className="space-y-4">
            <div className="bg-gradient-to-br from-blue-600 to-blue-800 rounded-xl p-6 text-white animate-fade-in-up">
              <Zap size={24} className="mb-3 opacity-90" />
              <h3 className="font-heading font-semibold text-lg mb-1">AI Chatbot</h3>
              <p className="text-blue-100 text-sm mb-4">AI botunuzu yapılandırın ve bilgi tabanı ekleyin.</p>
              <button
                onClick={() => navigate("/ai")}
                className="bg-white/20 hover:bg-white/30 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                data-testid="go-ai-btn"
              >
                Yapılandır →
              </button>
            </div>

            <div className="bg-white rounded-xl border border-slate-100 p-6 animate-fade-in-up">
              <Globe size={20} className="text-blue-500 mb-3" />
              <h3 className="font-heading font-semibold text-slate-900 mb-1">Site Ekle</h3>
              <p className="text-slate-500 text-sm mb-4">Web sitenize canlı destek ekleyin.</p>
              <button
                onClick={() => navigate("/sites")}
                className="text-blue-600 text-sm font-medium hover:text-blue-700 flex items-center gap-1 transition-colors"
                data-testid="go-sites-btn"
              >
                Siteleri Yönet <ArrowRight size={14} />
              </button>
            </div>

            {stats?.agent_performance?.length > 0 && (
              <div className="bg-white rounded-xl border border-slate-100 p-6 animate-fade-in-up">
                <h3 className="font-heading font-semibold text-slate-900 mb-3 flex items-center gap-2">
                  <Users size={16} className="text-blue-500" /> Agent Performans
                </h3>
                <div className="space-y-2">
                  {stats.agent_performance.slice(0, 3).map((a) => (
                    <div key={a.agent_id} className="flex items-center justify-between text-sm">
                      <span className="text-slate-700 truncate">{a.name}</span>
                      <span className="font-semibold text-blue-600 ml-2">{a.resolution_rate}%</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
}
